import { useState, useMemo, useCallback } from "react";
import { View, Text, ActivityIndicator } from "react-native";
import { FlashList } from "@shopify/flash-list";
import { Stack, useLocalSearchParams } from "expo-router";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { ScreenContainer } from "@/shared/components/ui/screen-container";
import { BackButton } from "@/shared/components/ui/back-button";
import { Heading } from "@/shared/components/ui/heading";
import { ItemSeparator } from "@/shared/components/ui/item-separator";
import { SearchInput } from "@/shared/components/ui/search-input";
import { UnderlineTabs } from "@/shared/components/ui/underline-tabs";
import { useFollowersList } from "@/features/profile/api/followers";
import type { FollowProfile } from "@/features/profile/api/followers";
import { useProfile } from "@/features/profile/api/profile";
import { UserListItem } from "@/features/profile/components/user-list-item";

const TABS = [
  { key: "followers" as const, label: "Seguidores" },
  { key: "following" as const, label: "Seguindo" },
] as const;

type Tab = (typeof TABS)[number]["key"];

export default function FollowersListScreen() {
  const { userId, tab: initialTab } = useLocalSearchParams<{
    userId: string;
    tab: "followers" | "following";
  }>();
  const insets = useSafeAreaInsets();
  const [activeTab, setActiveTab] = useState<Tab>(
    initialTab === "following" ? "following" : "followers"
  );
  const [searchQuery, setSearchQuery] = useState("");

  const { data: userProfile } = useProfile(userId);
  const {
    data: profiles,
    isLoading,
    error,
  } = useFollowersList(userId, activeTab);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return profiles;
    const lower = searchQuery.toLowerCase();
    return profiles?.filter(
      (p) =>
        p.displayName?.toLowerCase().includes(lower) ||
        p.username.toLowerCase().includes(lower)
    );
  }, [profiles, searchQuery]);

  const renderProfile = useCallback(
    ({ item }: { item: FollowProfile }) => (
      <UserListItem
        userId={item.userId}
        name={item.displayName || item.username}
        username={item.username}
        avatarUrl={item.avatarUrl}
      />
    ),
    []
  );

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <ScreenContainer style={{ paddingTop: 0 }}>
        {/* Header */}
        <View
          className="flex-row items-center justify-between px-4 pb-4"
          style={{ paddingTop: insets.top || 24 }}
        >
          <BackButton />
          <Heading variant="section" accessibilityRole="header">
            @{userProfile?.username ?? "..."}
          </Heading>
          <View className="h-10 w-10" />
        </View>

        <UnderlineTabs
          items={TABS}
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            setSearchQuery("");
          }}
        />

        <View className="px-5 pt-4 pb-2">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Pesquisar"
          />
        </View>

        {isLoading ? (
          <ActivityIndicator
            size="small"
            className="text-brand mt-4"
            accessibilityLabel="Loading"
          />
        ) : error ? (
          <Text
            className="text-destructive px-5 pt-4 text-sm italic"
            accessibilityRole="alert"
          >
            Falha ao carregar
          </Text>
        ) : (
          <FlashList
            data={filtered ?? []}
            keyExtractor={(item) => item.userId}
            renderItem={renderProfile}
            contentInsetAdjustmentBehavior="automatic"
            ListEmptyComponent={
              <Text className="text-muted-foreground px-5 pt-4 text-sm italic">
                {activeTab === "followers"
                  ? "Sem seguidores ainda."
                  : "Não está seguindo ninguém ainda."}
              </Text>
            }
            ItemSeparatorComponent={ItemSeparator}
            contentContainerStyle={{ padding: 20 }}
            showsVerticalScrollIndicator={false}
          />
        )}
      </ScreenContainer>
    </>
  );
}
